import { SettingsRepository } from '@/modules/parkManagement/repositories/Settings.repository'
import {
  FuelType,
  GetDriversPercentsRequest,
  ParkMode,
  RetentionBorders,
  RetentionType,
  SurchargeType,
  UpdateCommonSettingsRequest,
  UpdateDriversPercentsRequest,
  UpdateFinesRequest,
  UpdateMileageServiceRequest,
  UpdateRentSettingsRequest,
  UpdateTimeZoneRequest,
  UpdateTimeZoneResponse,
} from 'rikroot-types/parksmanagement/settings.pb'
import {
  MergedMinMaxRetentionsType,
  MileageServiceType,
  SettingsDataConvertType,
  SettingsDataType,
} from '@/core/types/settings/Settings.model'
import { showDefaultErrorMessage } from '@/core/utils/createMessage'
import { Actions, createMapper, Getters, Module, Mutations } from 'vuex-smart-module'
import Api from '@/repository'
import { Message } from 'element-ui'

const settingHelpers = () => {
  const convertSettingsFieldsToJSON = (settings: SettingsDataConvertType): SettingsDataType => {
    // конвертирует настройки в json формат
    const { fines, minMaxRetentions, driversPercents, driversPercentsSelfEmployed } = settings

    const checkFieldAndConvert = (field: string | RetentionBorders) => {
      try {
        if (typeof field === 'string') return JSON.parse(field)
      } catch (error) {
        return field
      }
      return field
    }

    return {
      ...settings,
      fines: checkFieldAndConvert(fines!),
      minMaxRetentions: checkFieldAndConvert(minMaxRetentions!),
      driversPercents: checkFieldAndConvert(driversPercents!),
      driversPercentsSelfEmployed: checkFieldAndConvert(driversPercentsSelfEmployed!),
    }
  }

  return {
    convertSettingsFieldsToJSON,
  }
}

const settingsRepository = new SettingsRepository()

const { convertSettingsFieldsToJSON } = settingHelpers()

class SettingsState {
  settingsData: SettingsDataType = {}
  retentionMinMax: RetentionBorders = {}
  mileageService: MileageServiceType = {}
}

class SettingsGetters extends Getters<SettingsState> {
  getRetentionTypes(status?: boolean) {
    // Возвращает объект, где ключи - это id удержаний если status - false, и где ключи  - name удержаний

    if (status) {
      return this.state.settingsData?.retentionTypes?.reduce(
        (acc: Record<string | number, Omit<RetentionType, 'name'>>, currentRetention) => {
          const { name, ...retention } = currentRetention

          if (name) {
            acc[name] = retention
          }

          return acc
        },
        {},
      )
    }

    if (!status) {
      return this.state.settingsData?.retentionTypes?.reduce(
        (acc: Record<string | number, Omit<RetentionType, 'id'>>, currentRetention) => {
          const { id, ...retention } = currentRetention

          if (id) {
            acc[id] = retention
          }

          return acc
        },
        {},
      )
    }
  }

  getSurchargesSystemTypes() {
    return this.state.settingsData.surchargeSystemTypes?.reduce(
      (acc: Record<string | number, Omit<SurchargeType, 'id'>>, currentSurcharge) => {
        const { id, ...surcharge } = currentSurcharge

        if (id) {
          acc[id] = surcharge
        }

        return acc
      },
      {},
    )
  }

  getSurchargesTypes() {
    // Возвращает объект, где ключи - это id доплат
    return this.state.settingsData.surchargeTypes?.reduce(
      (acc: Record<string | number, Omit<SurchargeType, 'id'>>, currentSurcharge) => {
        const { id, ...surcharge } = currentSurcharge

        if (id) {
          acc[id] = surcharge
        }

        return acc
      },
      {},
    )
  }

  getFuelTypes() {
    // Возвращает объект, где ключи - это id топлива
    return this.state.settingsData.fuelTypes?.reduce(
      (acc: Record<string | number, Omit<FuelType, 'id'>>, currentFuel) => {
        const { id, ...surcharge } = currentFuel
        if (id) {
          acc[id] = surcharge
        }

        return acc
      },
      {},
    )
  }

  getFines() {
    // Возвращает объект, где ключи - это name штрафа
    return this.state.settingsData.fines?.reduce((acc, currentFines) => {
      const name = Object.keys(currentFines)[0]

      if (name) {
        acc[name] = currentFines[name]
      }

      return acc
    }, {})
  }

  getAutoExecRetentionIds() {
    // Вовзвращает айдишники
    return this.state.settingsData.retentionTypes?.filter((r) => r.autoExec).map((r) => r.id)
  }

  getTimeZone() {
    // Вовзвращает таймзону
    return this.state.settingsData.timeZone
  }

  getMergeMinMaxRetentions = () => {
    // сливает максимальные и минимальные удержания
    const { mins, maxes } = this.state.settingsData.minMaxRetentions || {}

    if (!mins || !maxes) return

    const allValues = [...mins, ...maxes]
    const uniqueNames = [...new Set(allValues.map((item) => item.name))].filter(Boolean)

    return uniqueNames.map((name) => {
      const min = mins.find((item) => item.name === name)?.value || 0
      const max = maxes.find((item) => item.name === name)?.value || 0

      return {
        name,
        min,
        max,
      }
    })
  }

  getDriverSurchargeRetentionsIds = () => {
    // Вовзвращает  айдишки доплат
    return this.state.settingsData.retentionTypes?.filter((r) => r.driverSurcharge).map((r) => r.id)
  }

  getTaximeterLink(driverId: string) {
    // возвращает ссылку на таксометр с айди водилы, которые прокидывается пропсом
    return `https://fleet.taxi.yandex.ru/drivers/${driverId}/parks/${this.getters.getParkTaxiServiceId()}/card/`
  }

  getTaximeterLinkTemporary(driverId: string) {
    // возвращает ссылку на таксометр, но с другого домена (временно)

    return `https://fleet.yandex.ru/drivers/${driverId}/parks/${this.getters.getParkTaxiServiceId()}/card/`
  }

  getTaximeterLinkOrders(driverId: string) {
    return `https://fleet.taxi.yandex.ru/drivers/${driverId}/orders?park_id=${this.getters.getParkTaxiServiceId()}`
  }

  getTaximeterLinkOrdersTemporary(driverId: string) {
    return `https://fleet.yandex.ru/drivers/${driverId}/orders?park_id=${this.getters.getParkTaxiServiceId()}`
  }

  getControlRoomLink(serviceId: string) {
    return `https://fleet.yandex.ru/vehicles/${serviceId}/details?park_id=${this.getters.getParkTaxiServiceId()}`
  }

  getParkTaxiServiceId() {
    // возвращает айди сервиса такси
    return this.state.settingsData.parkTaxiServiceId
  }

  getMinMaxMergedTypes(): MergedMinMaxRetentionsType[] | undefined {
    // изменяет мин и макс удержания и убирает дубликаты
    if (!this.state.settingsData) return []

    const { mins, maxes } = this.state.retentionMinMax || {}

    if (!mins || !maxes) return

    const allValues = [...mins, ...maxes]
    const uniqueNames = [...new Set(allValues.map((item) => item.name))].filter(Boolean)

    return uniqueNames.map((name) => {
      const min = mins.find((item) => item.name === name)?.value || 0
      const max = maxes.find((item) => item.name === name)?.value || 0

      return {
        name: String(name),
        min,
        max,
      }
    })
  }

  isSerbiaMode(): boolean {
    return this.state.settingsData.mode === ParkMode.SERBIA
  }
}

class SettingsMutations extends Mutations<SettingsState> {
  SET_SETTINGS(settings: SettingsDataType) {
    // сетит настройки в стейт
    this.state.settingsData = settings
  }

  SET_MIN_MAX_RETENTIONS(minMaxRetentions: RetentionBorders) {
    // сетит удержания в стейт
    this.state.retentionMinMax = minMaxRetentions
  }

  SET_MILEAGE_SERVICE(mileageService: MileageServiceType) {
    // сетит настройки по пробегам в стейт
    this.state.mileageService = mileageService
  }

  UPDATE_SETTINGS_TIMEZONE(data: UpdateTimeZoneResponse) {
    // сетит все настройки, а затем заменяет только то, что в дате
    this.state.settingsData = {
      ...this.state.settingsData,
      ...data,
    }
  }
}

class SettingsActions extends Actions<
  SettingsState,
  SettingsGetters,
  SettingsMutations,
  SettingsActions
> {
  async fetchAllSettings(data?: string) {
    // получаем все настройки по парку вместе с процентными сетками или глобально. делаем одну структуру из них
    try {
      const [resSettings, resPercents] = await Promise.all([
        settingsRepository.getAllSettings({ regionParkId: data }),
        settingsRepository.getDriversPercents({ regionParkId: data }),
      ])
      const getConvertedSettings = convertSettingsFieldsToJSON({
        ...resSettings,
        fixedPercent: resSettings.fixedPercent! * 100,
        finesDiscountPercent: String(Number(resSettings.finesDiscountPercent!) * 100),
        driversPercents: resPercents?.driversPercents,
        driversPercentsSelfEmployed: resPercents?.driversPercentsSelfEmployed,
      })

      this.commit('SET_SETTINGS', getConvertedSettings)
      this.commit('SET_MIN_MAX_RETENTIONS', getConvertedSettings.minMaxRetentions!)

      return getConvertedSettings
    } catch (e) {
      showDefaultErrorMessage()

      return Promise.reject(e)
    }
  }

  async updateSettingsRent(data: UpdateRentSettingsRequest) {
    // обновляем настройки для рент парков
    try {
      return await settingsRepository.updateSettingsRent(data)
    } catch (err) {
      showDefaultErrorMessage()

      return Promise.reject(err)
    }
  }

  async updateSettingsCommon(data: UpdateCommonSettingsRequest) {
    // обновляем настройки для рент парков
    try {
      const result = await settingsRepository.updateCommonSettings(data)
      const { fixedPercent, ...rest } = data
      this.commit('SET_SETTINGS', {
        ...this.state.settingsData,
        ...rest,
        ...(fixedPercent !== undefined ? { fixedPercent: fixedPercent * 100 } : {}),
      })
      return result
    } catch (err) {
      showDefaultErrorMessage()

      return Promise.reject(err)
    }
  }

  async updateFines(data: UpdateFinesRequest) {
    // обновляет штрафы
    try {
      await settingsRepository.updateFines(data)
    } catch (err) {
      showDefaultErrorMessage()

      return Promise.reject(err)
    }
  }

  async updateDriversPercents(data: UpdateDriversPercentsRequest) {
    // обновляет прцоенты для водителей
    try {
      return await settingsRepository.updateDriversPercents(data)
    } catch (err) {
      showDefaultErrorMessage()

      return Promise.reject(err)
    }
  }

  async fetchMileageService() {
    // получаем настройки по пробегам, crm
    try {
      const response = await settingsRepository.getServiceMileage()

      this.commit('SET_MILEAGE_SERVICE', response)

      return response
    } catch (e) {
      showDefaultErrorMessage()

      return Promise.reject(e)
    }
  }

  async updateMileageService(data: UpdateMileageServiceRequest) {
    // обновляет способ сбора пробегом в настройках crm
    try {
      await settingsRepository.updateServiceMileage(data)

      Message({
        message: 'Способ сбора пробега успешно изменен!',
        type: 'success',
        duration: 5 * 1000,
      })
    } catch (err) {
      showDefaultErrorMessage()

      return Promise.reject(err)
    }
  }

  async fetchGetDriverPercentsRegionPark(data: GetDriversPercentsRequest) {
    // получает проценты водителей по парку
    try {
      const response = await settingsRepository.getDriversPercents(data)

      return convertSettingsFieldsToJSON({
        driversPercents: response?.driversPercents,
        driversPercentsSelfEmployed: response?.driversPercentsSelfEmployed,
      })
    } catch (err) {
      showDefaultErrorMessage()
      return Promise.reject(err)
    }
  }

  async fetchUpdateTimeZone(timeZone: UpdateTimeZoneRequest) {
    // обновляет тамзону глобального парка
    try {
      const response = await settingsRepository.updateTimeZone(timeZone)

      this.commit('UPDATE_SETTINGS_TIMEZONE', response)

      Message({
        message: 'Успешно обновлено!',
        type: 'success',
        duration: 5 * 1000,
      })
    } catch (err) {
      showDefaultErrorMessage()

      return Promise.reject(err)
    }
  }
}

export const globalSettings = new Module({
  namespaced: true,
  state: SettingsState,
  getters: SettingsGetters,
  mutations: SettingsMutations,
  actions: SettingsActions,
})

export const settingsMapper = createMapper(globalSettings)
